/**
 * Session-only undo/redo. The stack lives in memory inside the editor host;
 * navigating away or reloading clears it. Persisting undo across reloads
 * adds complexity for a feature few users will notice (architectural call).
 *
 * Capacity caps the working memory for long sessions; older entries fall off
 * the bottom and become unreachable. The cap is generous (200 commands)
 * because edits are tiny.
 */
import type { Command } from './commands'

export interface UndoEntry {
  command: Command
  inverse: Command
}

const DEFAULT_CAP = 200

export class UndoStack {
  private undoEntries: UndoEntry[] = []
  private redoEntries: UndoEntry[] = []

  constructor(private readonly cap: number = DEFAULT_CAP) {}

  push(command: Command, inverse: Command): void {
    this.undoEntries.push({ command, inverse })
    if (this.undoEntries.length > this.cap) {
      this.undoEntries.shift()
    }
    // A new edit invalidates everything that was undone — the future is now
    // a different timeline.
    this.redoEntries = []
  }

  undo(): UndoEntry | null {
    const entry = this.undoEntries.pop()
    if (!entry) return null
    this.redoEntries.push(entry)
    return entry
  }

  redo(): UndoEntry | null {
    const entry = this.redoEntries.pop()
    if (!entry) return null
    this.undoEntries.push(entry)
    return entry
  }

  canUndo(): boolean {
    return this.undoEntries.length > 0
  }

  canRedo(): boolean {
    return this.redoEntries.length > 0
  }

  clear(): void {
    this.undoEntries = []
    this.redoEntries = []
  }

  /** Test/debug surface — number of entries in each stack. */
  size(): { undo: number; redo: number } {
    return { undo: this.undoEntries.length, redo: this.redoEntries.length }
  }
}
